import React, { useEffect, useMemo, useState } from 'react';
import { listAll, STATUS_LABELS } from '@/lib/adminApi';

const KIND_LABEL = { contacts: 'Contact', speaking: 'Speaking', newsletter: 'Newsletter', waitlist: 'Waitlist' };

export default function AllSubmissions() {
  const [filters, setFilters] = useState({ kind: 'all', search: '', page: 1 });
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { page: filters.page, limit: 25 };
    if (filters.kind && filters.kind !== 'all') p.kind = filters.kind;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    listAll(params)
      .then(setData)
      .finally(() => setLoading(false));
  }, [params]);

  return (
    <div data-testid="admin-all-submissions">
      <header className="mb-8">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Unified feed</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
          All Submissions
        </h1>
      </header>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <input
          data-testid="all-search-input"
          placeholder="Search…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          className="flex-1 bg-white/80 border border-neutral-200 rounded-lg px-4 py-2.5 text-[14px] focus:outline-none focus:border-neutral-900"
        />
        <select
          data-testid="all-kind-filter"
          value={filters.kind}
          onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value, page: 1 }))}
          className="bg-white/80 border border-neutral-200 rounded-lg px-3 py-2.5 text-[13px]"
        >
          <option value="all">All types</option>
          <option value="contacts">Contact</option>
          <option value="speaking">Speaking</option>
          <option value="newsletter">Newsletter</option>
          <option value="waitlist">Waitlist</option>
        </select>
      </div>

      <div className="bg-white/70 border border-neutral-200/60 rounded-xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50/70">
            <tr>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Type</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Name / Email</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Detail</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Status</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200/60">
            {loading && (
              <tr><td colSpan={5} className="px-5 py-10 text-center uppercase tracking-[0.2em] text-[11px] text-neutral-500">Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-14 text-center text-neutral-500">No records match.</td></tr>
            )}
            {!loading && data.items.map((it, i) => (
              <tr key={`${it.type}-${it.id}-${i}`} className="hover:bg-neutral-50">
                <td className="px-5 py-3">
                  <span className="uppercase tracking-[0.2em] text-[10px] text-[#C8A15A]">{KIND_LABEL[it.type] || it.type}</span>
                </td>
                <td className="px-5 py-3">
                  <div className="text-neutral-900">
                    {it.name || `${it.first_name || ''} ${it.last_name || ''}`.trim() || it.email}
                  </div>
                  <div className="text-neutral-500 text-[11px]">{it.email}</div>
                </td>
                <td className="px-5 py-3 text-neutral-700">
                  {it.organization || it.subject || it.product || it.event_name || '—'}
                </td>
                <td className="px-5 py-3 text-neutral-700">{STATUS_LABELS[it.status] || it.status || '—'}</td>
                <td className="px-5 py-3 text-neutral-500 text-[11px]">
                  {it.created_at ? new Date(it.created_at).toLocaleString() : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-6 text-[12px] text-neutral-500">
        <div>{data.total} record{data.total === 1 ? '' : 's'}</div>
        <div className="flex items-center gap-2">
          <button
            disabled={data.page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
            className="px-3 py-1.5 rounded border border-neutral-200 disabled:opacity-40"
          >
            ‹ Prev
          </button>
          <span>{data.page} / {data.pages || 1}</span>
          <button
            disabled={data.page >= data.pages}
            onClick={() => setFilters((f) => ({ ...f, page: Math.min(data.pages, f.page + 1) }))}
            className="px-3 py-1.5 rounded border border-neutral-200 disabled:opacity-40"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
